"use client"

import { useState, useEffect } from "react"
import { motion, useScroll, useTransform } from "framer-motion"
import { Github, Linkedin, Mail, ArrowUpRight } from "lucide-react"
import "./App.css"

const Button = ({ children, className = "", variant = "default", size = "default", asChild = false, ...props }) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background"
  const variants = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    outline: "border border-input hover:bg-accent hover:text-accent-foreground",
    ghost: "hover:bg-accent hover:text-accent-foreground",
  }
  const sizes = {
    default: "h-10 py-2 px-4",
    sm: "h-9 px-3 rounded-md",
    lg: "h-11 px-8 rounded-md",
  }
  const classes = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`

  if (asChild) {
    return <span className={classes}>{children}</span>
  }
  return (
    <button className={classes} {...props}>
      {children}
    </button>
  )
}

export default function App() {
  const [activeSection, setActiveSection] = useState("about")
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const { scrollYProgress } = useScroll()
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"])

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY })
    }

    const handleScroll = () => {
      const sections = ["about", "experience", "projects", "education", "certifications", "honors", "contact"]
      const scrollPosition = window.scrollY + 100

      for (const section of sections) {
        const element = document.getElementById(section)
        if (element) {
          const offsetTop = element.offsetTop
          const offsetHeight = element.offsetHeight
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section)
            break
          }
        }
      }
    }

    window.addEventListener("mousemove", handleMouseMove)
    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const experiences = [
    {
      period: "MAR 25 - Present",
      image: "./work3.png",
      imageLink: "https://www.linkedin.com/feed/update/urn:li:ugcPost:7340660692997283840/", // Link to certificate/document
      title: "Full Stack Developer",
      company: "Prime Study Abroad",
      description:
        "Prime Study Abroad streamlines the study abroad process with secure document management, user authentication, and role-based access. Built using the MERN stack, it supports over 1,000 users and handles 10,000+ file uploads efficiently. As a developer, I learned advanced cloud deployment, security best practices, and scalable architecture design.",
      technologies: ["AWS", "MERN Stack", "CRMs", "Tailwind CSS"],
      link: "https://www.linkedin.com/company/prime-study-abroad/",
    },
    {
      period: "SEPT 2024 — DEC 2024",
      image: "./work1.jpeg",
      imageLink: "https://showcase-anni02th.netlify.app/image/6850160d27eb77b68098128c", // Link to certificate/document
      title: "Web Developer Intern",
      company: "Skill Enhanced Pvt. Ltd.",
      description:
        "Led a development team, delivering an e-commerce platform and company website with 40% faster completion rate. Developed responsive WordPress websites integrating plugins, improving site performance by 35%. Built and optimized backend systems using PHP and MySQL, managing 10,000+ records while maintaining 99.9% uptime.",
      technologies: ["PHP", "MySQL", "WordPress", "JavaScript", "CSS3"],
      link: "https://www.linkedin.com/company/skillenhanced-private-limited/",
    },
    {
      period: "JUL 2024 — SEPT 2024",
      image: "./work2.jpg",
      imageLink: "https://showcase-anni02th.netlify.app/image/685015ce27eb77b68098127d", // Link to certificate/document
      title: "Frontend Developer Intern",
      company: "To Let Globe",
      description:
        "Developed company's web platform using MERN stack, resulting in 50% increase in user engagement. Built responsive UI components with React.js and Tailwind CSS, reducing load time by 40%. Collaborated with 20-member team using Git and JIRA, delivering features ahead of schedule.",
      technologies: ["React", "Node.js", "MongoDB", "Tailwind CSS", "Git", "JIRA"],
      link: "https://toletglobe.com",
    },
  ]

  const projects = [
    {
      title: "ERP Website for My College",
      description:
        "Developed a comprehensive college ERP system featuring user authentication and database management with MongoDB, utilizing React and Python Flask for a full-stack solution.",
      technologies: ["MongoDB", "React", "Vite", "Python", "Tailwind CSS", "Node.js"],
      github: "https://github.com/anni02th/Webathon-Mumbai",
      external: "https://webathon-anni.netlify.app/",
      image: "./img1.png",
      imageLink: "https://drive.google.com/file/d/1example-project-screenshot1/view", // Link to full screenshot/demo
    },
    {
      title: "QRide",
      description:
        "Created a user-friendly platform for renting vehicles on campus or corporate locations, implementing features such as tracking and dynamic payment authentication using React.js and JavaScript.",
      technologies: ["MongoDB", "React.js", "Express.js", "Tailwind CSS", "Node.js"],
      github: "https://github.com/aniketdagamore/qride",
      external: "https://qrides.netlify.app",
      image: "./img2.png",
      imageLink: "https://drive.google.com/file/d/1example-project-screenshot2/view", // Link to full screenshot/demo
    },
    {
      title: "KKnotes",
      description:
        "Developed a dynamic website using the MERN stack from scratch, enabling college students to upload, download, and study notes. Integrated Google AdSense for monetization.",
      technologies: ["MongoDB", "React.js", "Express.js", "Tailwind CSS", "Node.js", "Google AdSense"],
      github: "https://github.com/aniketdagamore/kknotes",
      external: "https://kknotes.netlify.app/",
      image: "./img3.png",
      imageLink: "https://drive.google.com/file/d/1example-project-screenshot3/view", // Link to full screenshot/demo
    },
  ]

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: "smooth" })
    }
  }

  return (
    <div className="min-h-screen bg-amber-50 text-gray-900 font-serif">
      {/* Subtle cursor glow effect */}
      <motion.div
        className="fixed pointer-events-none z-50 w-64 h-64 rounded-full opacity-20 blur-3xl bg-gradient-to-r from-yellow-400 to-amber-500 transition-all duration-700 ease-out"
        style={{
          left: mousePosition.x - 128,
          top: mousePosition.y - 128,
        }}
      />

      <div className="lg:flex lg:justify-between lg:gap-4 mx-auto min-h-screen max-w-screen-xl px-6 py-12 md:px-12 md:py-20 lg:px-24 lg:py-0">
        {/* Left Column - Fixed */}
        <div className="lg:sticky lg:top-0 lg:flex lg:max-h-screen lg:w-1/2 lg:flex-col lg:justify-between lg:py-12">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <h1 className="text-4xl font-light tracking-tight text-gray-900 sm:text-5xl lg:text-6xl leading-tight">
                <motion.span
                  className="block"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.2 }}
                >
                  ANIKET
                </motion.span>
                <motion.span
                  className="block text-yellow-600 font-normal"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.4 }}
                >
                  DAGA MORE
                </motion.span>
              </h1>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.6 }}
                className="mt-6"
              >
                <p className="text-base font-light text-gray-700 leading-relaxed">
                  Full Stack Web Developer / Computer Science Student /
                  <br />
                  Problem Solver and Partner.
                </p>
                <p className="mt-3 text-sm font-light text-gray-600 leading-relaxed max-w-sm">
                  Currently pursuing B.Tech in Computer Science and Design at{" "}
                  <a
                    href="https://www.kkwagh.edu.in/"
                    className="underline decoration-yellow-400 decoration-2 underline-offset-2 hover:decoration-yellow-600 transition-colors duration-300"
                  >
                    K.K. Wagh Institute
                  </a>
                  .
                </p>
                <Button
                  variant="outline"
                  className="mt-6 bg-yellow-400 text-white font-bold hover:bg-yellow-50 hover:border-yellow-500 tracking-wide transition-all duration-300"
                  asChild>
                  <a href="https://anni02th-3d.netlify.app/" >3D Portfolio</a>
                </Button>
              </motion.div>
            </motion.div>

            {/* Navigation */}
            <nav className="nav hidden lg:block" aria-label="In-page jump links">
              <motion.ul
                className="mt-12 w-max scroll-y-hidden lg:block lg:overflow-y-auto lg:pr-6 lg:pb-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.8 }}
              >
                {[
                  { id: "about", label: "ABOUT" },
                  { id: "experience", label: "EXPERIENCE" },
                  { id: "projects", label: "WORK" },
                  { id: "education", label: "EDUCATION" },
                  { id: "certifications", label: "CERTIFICATIONS" },
                  { id: "honors", label: "HONORS" },
                  { id: "contact", label: "CONTACT" },
                ].map((section, index) => (
                  <motion.li
                    key={section.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.9 + index * 0.1 }}
                  >
                    <a
                      className={`group flex items-center py-3 transition-all duration-500 ease-out ${
                        activeSection === section.id ? "active" : ""
                      }`}
                      href={`#${section.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        scrollToSection(section.id)
                      }}
                    >
                      <span
                        className={`nav-indicator mr-4 h-px transition-all duration-500 ease-out ${
                          activeSection === section.id
                            ? "w-12 bg-yellow-500"
                            : "w-6 bg-gray-400 group-hover:w-12 group-hover:bg-yellow-400"
                        }`}
                      ></span>
                      <span
                        className={`nav-text text-xs font-light tracking-[0.2em] transition-all duration-500 ease-out ${
                          activeSection === section.id
                            ? "text-yellow-600 font-normal"
                            : "text-gray-500 group-hover:text-yellow-600 group-hover:font-normal"
                        }`}
                      >
                        {section.label}
                      </span>
                    </a>
                  </motion.li>
                ))}
              </motion.ul>
            </nav>
          </div>

          {/* Social Links */}
          <motion.ul
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="ml-1 flex items-center"
            aria-label="Social media"
          >
            {[
              { icon: Github, href: "https://github.com/anni02th", label: "GitHub" },
              { icon: Linkedin, href: "https://www.linkedin.com/in/aniket-more-b23b24255/", label: "LinkedIn" },
              { icon: Mail, href: "mailto:work.anni02th@gmail.com", label: "Email" },
            ].map(({ icon: Icon, href, label }, index) => (
              <motion.li
                key={label}
                className="mr-5 text-xs"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 1.3 + index * 0.1 }}
              >
                <a
                  className="block hover:text-yellow-600 transition-all duration-300 ease-out p-2 -m-2 hover:scale-110"
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                >
                  <Icon className="h-6 w-6" />
                </a>
              </motion.li>
            ))}
          </motion.ul>
        </div>

        {/* Right Column - Scrollable Content */}
        <div className="pt-24 lg:w-1/2 lg:py-24">
          {/* About Section */}
          <section id="about" className="mb-16 scroll-mt-16 md:mb-24 lg:mb-36 lg:scroll-mt-24">
            <div className="sticky top-0 z-20 -mx-6 mb-4 w-screen bg-amber-50/90 px-6 py-5 backdrop-blur-sm md:-mx-12 md:px-12 lg:sr-only lg:relative lg:top-auto lg:mx-auto lg:w-full lg:px-0 lg:py-0 lg:opacity-0">
              <h2 className="text-sm font-light tracking-[0.2em] text-yellow-600 lg:sr-only">ABOUT</h2>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
            >
              <p className="mb-6 text-gray-700 leading-relaxed text-lg font-light">
                I'm a passionate full-stack developer and Computer Science student with hands-on experience in building
                scalable web applications. Currently pursuing{" "}
                <span className="text-yellow-700 font-normal">B.Tech in Computer Science and Design</span> at K.K. Wagh
                Institute of Engineering Education and Research, Nashik.
              </p>
              <p className="mb-6 text-gray-700 leading-relaxed text-lg font-light">
                My main focus these days is building accessible, inclusive products and digital experiences for a
                variety of clients. I most enjoy working on projects that challenge me to learn something new and push
                the boundaries of what's possible with modern web technologies.
              </p>
              <p className="text-gray-700 leading-relaxed text-lg font-light">
                When I'm at the computer, I'm usually reading tech blogs, exploring new frameworks, or working on
                personal projects that solve real-world problems.
              </p>
            </motion.div>
          </section>

          {/* Experience Section */}
          <section id="experience" className="mb-16 scroll-mt-16 md:mb-24 lg:mb-36 lg:scroll-mt-24">
            <div className="sticky top-0 z-20 -mx-6 mb-4 w-screen bg-amber-50/90 px-6 py-5 backdrop-blur-sm md:-mx-12 md:px-12 lg:sr-only lg:relative lg:top-auto lg:mx-auto lg:w-full lg:px-0 lg:py-0 lg:opacity-0">
              <h2 className="text-sm font-light tracking-[0.2em] text-yellow-600 lg:sr-only">EXPERIENCE</h2>
            </div>
            <div>
              <ol className="group/list">
                {experiences.map((experience, index) => (
                  <motion.li
                    key={index}
                    className="mb-12"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                    viewport={{ once: true }}
                  >
                    <div className="group relative grid pb-1 transition-all duration-500 ease-out sm:grid-cols-8 sm:gap-8 md:gap-4 lg:hover:!opacity-100 lg:group-hover/list:opacity-50">
                      <div className="absolute -inset-x-4 -inset-y-4 z-0 hidden rounded-md transition-all duration-500 ease-out motion-reduce:transition-none lg:-inset-x-6 lg:block lg:group-hover:bg-yellow-50/50 lg:group-hover:shadow-[inset_0_1px_0_0_rgba(251,191,36,0.1)] lg:group-hover:drop-shadow-lg"></div>
                      <header
                        className="z-10 mb-2 mt-1 text-xs font-light tracking-wide text-gray-500 sm:col-span-2"
                        aria-label={experience.period}
                      >
                        {experience.period}
                        <br />
                        {/* Image links to certificate/document */}
                        <a
                          href={experience.imageLink}
                          target="_blank"
                          rel="noreferrer"
                          className="block mt-4 group/img"
                          title="View Certificate/Document"
                        >
                          <img
                            alt={`${experience.title} certificate`}
                            loading="lazy"
                            width="300"
                            height="200"
                            decoding="async"
                            className="w-full h-auto object-cover transition-all duration-500 ease-out group-hover/img:scale-105 cursor-pointer"
                            src={experience.image || "/placeholder.svg"}
                          />
                        </a>
                      </header>
                      <div className="z-10 sm:col-span-6">
                        <h3 className="font-normal leading-snug text-gray-900">
                          <div>
                            {/* Title links to company website */}
                            <a
                              className="inline-flex items-baseline font-normal leading-tight text-gray-900 hover:text-yellow-700 focus-visible:text-yellow-700 group/link text-base transition-colors duration-300"
                              href={experience.link}
                              target="_blank"
                              rel="noreferrer"
                              aria-label={`${experience.title} at ${experience.company}`}
                            >
                              <span className="absolute -inset-x-4 -inset-y-2.5 hidden rounded md:-inset-x-6 md:-inset-y-4"></span>
                              <span>
                                {experience.title} ·{" "}
                                <span className="inline-block">
                                  {experience.company}
                                  <ArrowUpRight className="inline-block h-4 w-4 shrink-0 transition-transform duration-300 group-hover/link:-translate-y-1 group-hover/link:translate-x-1 group-focus-visible/link:-translate-y-1 group-focus-visible/link:translate-x-1 motion-reduce:transition-none ml-1 translate-y-px" />
                                </span>
                              </span>
                            </a>
                          </div>
                        </h3>
                        <p className="mt-2 text-sm leading-normal text-gray-600 font-light">{experience.description}</p>
                        <ul className="mt-2 flex flex-wrap" aria-label="Technologies used">
                          {experience.technologies.map((tech) => (
                            <li key={tech} className="mr-1.5 mt-2">
                              <div className="flex items-center rounded-full bg-yellow-100 px-3 py-1 text-xs font-light leading-5 text-yellow-800 border border-yellow-200">
                                {tech}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.li>
                ))}
              </ol>
              <motion.div
                className="mt-12 "
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                viewport={{ once: true }}
              >
                <Button
                  variant="outline"
                  className="border-yellow-400 text-yellow-700 hover:bg-yellow-50 hover:border-yellow-500 font-light tracking-wide transition-all duration-300"
                  asChild
                >
                  <a className="flex " href="/resume.pdf" target="_blank" rel="noopener noreferrer">
                    View Full Résumé
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </motion.div>
            </div>
          </section>

          {/* Projects Section */}
          <section id="projects" className="mb-16 scroll-mt-16 md:mb-24 lg:mb-36 lg:scroll-mt-24">
            <div className="sticky top-0 z-20 -mx-6 mb-4 w-screen bg-amber-50/90 px-6 py-5 backdrop-blur-sm md:-mx-12 md:px-12 lg:sr-only lg:relative lg:top-auto lg:mx-auto lg:w-full lg:px-0 lg:py-0 lg:opacity-0">
              <h2 className="text-sm font-light tracking-[0.2em] text-yellow-600 lg:sr-only">WORK</h2>
            </div>
            <div>
              <ul className="group/list">
                {projects.map((project, index) => (
                  <motion.li
                    key={index}
                    className="mb-12"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                    viewport={{ once: true }}
                  >
                    <div className="group relative grid gap-4 pb-1 transition-all duration-500 ease-out sm:grid-cols-8 sm:gap-8 md:gap-4 lg:hover:!opacity-100 lg:group-hover/list:opacity-50 lg:group-hover/list:[&_img]:!opacity-100">
                      <div className="absolute -inset-x-4 -inset-y-4 z-0 hidden rounded-md transition-all duration-500 ease-out motion-reduce:transition-none lg:-inset-x-6 lg:block lg:group-hover:bg-yellow-50/50 lg:group-hover:shadow-[inset_0_1px_0_0_rgba(251,191,36,0.1)] lg:group-hover:drop-shadow-lg"></div>
                      <div className="z-10 sm:order-2 sm:col-span-6">
                        <h3>
                          {/* Title links to live project */}
                          <a
                            className="inline-flex items-baseline font-normal leading-tight text-gray-900 hover:text-yellow-700 focus-visible:text-yellow-700 group/link text-base transition-colors duration-300"
                            href={project.external}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={project.title}
                          >
                            <span className="absolute -inset-x-4 -inset-y-2.5 hidden rounded md:-inset-x-6 md:-inset-y-4"></span>
                            <span>
                              {project.title}
                              <ArrowUpRight className="inline-block h-4 w-4 shrink-0 transition-transform duration-300 group-hover/link:-translate-y-1 group-hover/link:translate-x-1 group-focus-visible/link:-translate-y-1 group-focus-visible/link:translate-x-1 motion-reduce:transition-none ml-1 translate-y-px" />
                            </span>
                          </a>
                        </h3>
                        <p className="mt-2 text-sm leading-normal text-gray-600 font-light">{project.description}</p>
                        <ul className="mt-2 flex flex-wrap" aria-label="Technologies used">
                          {project.technologies.map((tech) => (
                            <li key={tech} className="mr-1.5 mt-2">
                              <div className="flex items-center rounded-full bg-yellow-100 px-3 py-1 text-xs font-light leading-5 text-yellow-800 border border-yellow-200">
                                {tech}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="relative group/image sm:order-1 sm:col-span-2 sm:translate-y-1">
                        {/* Image links to full screenshot/demo */}
                        <a
                          href={project.imageLink}
                          target="_blank"
                          rel="noreferrer"
                          className="block"
                          title="View Full Screenshot"
                        >
                          <div className="relative overflow-hidden rounded-lg border-2 border-gray-200 transition-all duration-500 ease-out group-hover/image:border-yellow-400 transform-gpu perspective-1000">
                            <div className="relative transition-all duration-500 ease-out group-hover/image:scale-105 group-hover/image:rotate-y-12 group-hover/image:rotate-x-6 transform-style-preserve-3d">
                              <img
                                alt={`${project.title} screenshot`}
                                loading="lazy"
                                width="300"
                                height="200"
                                decoding="async"
                                className="w-full h-auto object-cover transition-all duration-500 ease-out cursor-pointer"
                                src={project.image || "/placeholder.svg"}
                              />
                              <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/20 to-amber-600/20 opacity-0 group-hover/image:opacity-100 transition-opacity duration-500 ease-out"></div>
                              <div className="absolute inset-0 shadow-2xl opacity-0 group-hover/image:opacity-30 transition-opacity duration-500 ease-out"></div>
                            </div>
                          </div>
                          <div className="absolute -inset-2 bg-gradient-to-r from-yellow-400/20 to-amber-500/20 rounded-lg blur-lg opacity-0 group-hover/image:opacity-100 transition-opacity duration-500 ease-out -z-10"></div>
                        </a>
                      </div>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </div>
          </section>

          {/* Education Section */}
          <section id="education" className="mb-16 scroll-mt-16 md:mb-24 lg:mb-36 lg:scroll-mt-24">
            <div className="sticky top-0 z-20 -mx-6 mb-4 w-screen bg-amber-50/90 px-6 py-5 backdrop-blur-sm md:-mx-12 md:px-12 lg:sr-only lg:relative lg:top-auto lg:mx-auto lg:w-full lg:px-0 lg:py-0 lg:opacity-0">
              <h2 className="text-sm font-light tracking-[0.2em] text-yellow-600 lg:sr-only">EDUCATION</h2>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h3 className="text-2xl font-light text-gray-900 mb-8">Academic Background</h3>

              <div className="group relative grid pb-1 transition-all duration-500 ease-out sm:grid-cols-8 sm:gap-8 md:gap-4 lg:hover:!opacity-100">
                <div className="absolute -inset-x-4 -inset-y-4 z-0 hidden rounded-md transition-all duration-500 ease-out motion-reduce:transition-none lg:-inset-x-6 lg:block lg:group-hover:bg-yellow-50/50 lg:group-hover:shadow-[inset_0_1px_0_0_rgba(251,191,36,0.1)] lg:group-hover:drop-shadow-lg"></div>
                <header className="z-10 mb-2 mt-1 text-xs font-light tracking-wide text-gray-500 sm:col-span-2">
                  SEP 2022 — PRESENT
                </header>
                <div className="z-10 sm:col-span-6">
                  <h4 className="font-normal leading-snug text-gray-900 text-base mb-2">
                    B.Tech in Computer Science and Design
                  </h4>
                  <p className="text-sm text-yellow-700 font-normal mb-2">
                    K. K. Wagh Institute of Engineering Education and Research, Nashik
                  </p>
                  <div className="flex items-center gap-4 mb-3">
                    <span className="inline-flex items-center rounded-full bg-yellow-100 px-3 py-1 text-xs font-light leading-5 text-yellow-800 border border-yellow-200">
                      CGPA: 7.42
                    </span>
                  </div>
                  <p className="text-sm leading-normal text-gray-600 font-light">
                    Pursuing comprehensive education in computer science fundamentals, software design principles, and
                    modern development practices.
                  </p>
                </div>
              </div>
            </motion.div>
          </section>

          {/* Certifications Section */}
          <section id="certifications" className="mb-16 scroll-mt-16 md:mb-24 lg:mb-36 lg:scroll-mt-24">
            <div className="sticky top-0 z-20 -mx-6 mb-4 w-screen bg-amber-50/90 px-6 py-5 backdrop-blur-sm md:-mx-12 md:px-12 lg:sr-only lg:relative lg:top-auto lg:mx-auto lg:w-full lg:px-0 lg:py-0 lg:opacity-0">
              <h2 className="text-sm font-light tracking-[0.2em] text-yellow-600 lg:sr-only">CERTIFICATIONS</h2>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-light text-gray-900 mb-8">Professional Certifications</h3>
              <p className="text-lg font-light text-gray-600 mb-8 leading-relaxed">
                Continuous learning and skill development through recognized programs
              </p>

              <div className="space-y-8">
                {[
                  {
                    title: "Full Stack Development",
                    institution: "Udemy",
                    year: "2024",
                    description:
                      "Comprehensive course covering modern web development technologies including React, Node.js, and database management.",
                    skills: ["React", "Node.js", "MongoDB", "Express.js"],
                    certificateLink: "https://showcase-anni02th.netlify.app/image/685016fd27eb77b6809812b5",
                  },
                  {
                    title: "AWS Certified Solutions Architect",
                    institution: "AWS",
                    year: "2025",
                    description:
                      "Architecting and deploying scalable applications on AWS, focusing on best practices for security and reliability.",
                    skills: ["AWS", "Cloud Architecture", "Security", "Scalability"],
                    certificateLink: "https://showcase-anni02th.netlify.app/image/6850164127eb77b680981295",
                  },
                  {
                    title: "User Interface and Design",
                    institution: "IIT Roorkee, NPTEL",
                    year: "2023",
                    description:
                      "Principles of user-centered design, interface design patterns, and usability testing.",
                    skills: ["UI/UX Design", "Figma", "User Research", "Prototyping"],
                    certificateLink: "https://showcase-anni02th.netlify.app/image/6850173f27eb77b6809812c3",
                  },
                ].map((cert, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                    viewport={{ once: true }}
                    className="group relative grid pb-1 transition-all duration-500 ease-out sm:grid-cols-8 sm:gap-8 md:gap-4 lg:hover:!opacity-100"
                  >
                    <div className="absolute -inset-x-4 -inset-y-4 z-0 hidden rounded-md transition-all duration-500 ease-out motion-reduce:transition-none lg:-inset-x-6 lg:block lg:group-hover:bg-yellow-50/50 lg:group-hover:shadow-[inset_0_1px_0_0_rgba(251,191,36,0.1)] lg:group-hover:drop-shadow-lg"></div>
                    <header className="z-10 mb-2 mt-1 text-xs font-light tracking-wide text-gray-500 sm:col-span-2">
                      {cert.year}
                    </header>
                    <div className="z-10 sm:col-span-6">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-normal leading-snug text-gray-900 text-base mb-1">{cert.title}</h4>
                        <a
                          href={cert.certificateLink}
                          target="_blank"
                          rel="noreferrer"
                          className="text-yellow-600 hover:text-yellow-700 transition-colors duration-300 text-sm underline decoration-yellow-400 decoration-1 underline-offset-2"
                          title="View Certificate"
                        >
                          View Certificate
                        </a>
                      </div>
                      <p className="text-sm text-yellow-700 font-normal mb-3">{cert.institution}</p>
                      <p className="text-sm leading-normal text-gray-600 font-light mb-3">{cert.description}</p>
                      <ul className="flex flex-wrap gap-1.5" aria-label="Skills learned">
                        {cert.skills.map((skill) => (
                          <li key={skill}>
                            <div className="flex items-center rounded-full bg-yellow-100 px-3 py-1 text-xs font-light leading-5 text-yellow-800 border border-yellow-200">
                              {skill}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <br />
            <Button
                  variant="outline"
                  className="border-yellow-400 mt-4 text-yellow-700 hover:bg-yellow-50 hover:border-yellow-500 font-light tracking-wide transition-all duration-300"
                  asChild
                >
                  <a className="flex " href="https://showcase-anni02th.netlify.app/" target="_blank" rel="noopener noreferrer">
                    View All Certification
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
          </section>

          {/* Honors & Positions Section */}
          <section id="honors" className="mb-16 scroll-mt-16 md:mb-24 lg:mb-36 lg:scroll-mt-24">
            <div className="sticky top-0 z-20 -mx-6 mb-4 w-screen bg-amber-50/90 px-6 py-5 backdrop-blur-sm md:-mx-12 md:px-12 lg:sr-only lg:relative lg:top-auto lg:mx-auto lg:w-full lg:px-0 lg:py-0 lg:opacity-0">
              <h2 className="text-sm font-light tracking-[0.2em] text-yellow-600 lg:sr-only">HONORS</h2>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-light text-gray-900 mb-8">Honors & Leadership Positions</h3>
              <p className="text-lg font-light text-gray-600 mb-8 leading-relaxed">
                Recognition for academic excellence and leadership in technical communities
              </p>

              <div className="space-y-8">
                {[
                  {
                    title: "Reliance Foundation Scholar",
                    description: "All India Top 5000 Undergraduate Scholars",
                    year: "2022",
                    img: "/ext3.png",
                    type: "National Recognition",
                    details:
                      "Selected among the top 5000 undergraduate students across India for academic excellence and potential for leadership in technology and innovation.",
                    highlight: true,
                    certificateLink: "https://drive.google.com/file/d/1example-reliance-scholar-cert/view",
                  },
                  {
                    title: "Technical Head",
                    description: "TEDxKKWIERR International Level Event",
                    year: "2025",
                    img: "/ext1.png",
                    type: "Leadership Position",
                    details:
                      "Leading the technical team for the first-ever TEDx event in Nashik, managing website development, live streaming, and technical infrastructure for 1000+ attendees.",
                    highlight: false,
                    certificateLink: "https://drive.google.com/file/d/1example-tedx-cert/view",
                  },
                  {
                    title: "Website Lead",
                    description: "Innov-era National Level Hackathon",
                    year: "2025",
                    img: "/ext2.png",
                    type: "Technical Leadership",
                    details:
                      "Spearheading the development of the official hackathon website and registration platform, managing participant data and event logistics for 500+ participants.",
                    highlight: false,
                    certificateLink: "https://drive.google.com/file/d/1example-hackathon-cert/view",
                  },
                ].map((honor, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                    viewport={{ once: true }}
                    className="group relative grid pb-1 transition-all duration-500 ease-out sm:grid-cols-8 sm:gap-8 md:gap-4 lg:hover:!opacity-100"
                  >
                    <div
                      className={`absolute -inset-x-4 -inset-y-4 z-0 hidden rounded-md transition-all duration-500 ease-out motion-reduce:transition-none lg:-inset-x-6 lg:block ${honor.highlight ? "lg:group-hover:bg-yellow-100/60" : "lg:group-hover:bg-yellow-50/50"} lg:group-hover:shadow-[inset_0_1px_0_0_rgba(251,191,36,0.1)] lg:group-hover:drop-shadow-lg`}
                    ></div>
                    <header className="z-10 mb-2 mt-1 text-xs font-light tracking-wide text-gray-500 sm:col-span-2">
                      {honor.year}
                        <br />
                        {/* Image links to certificate/document */}
                        <a
                          href={honor.certificateLink}
                          target="_blank"
                          rel="noreferrer"
                          className="block mt-4 group/img"
                          title="View Certificate/Document"
                        >
                          <img
                            alt={`${honor.highlight} certificate`}
                            loading="lazy"
                            width="300"
                            height="200"
                            decoding="async"
                            className="w-full h-auto object-cover transition-all duration-500 ease-out group-hover/img:scale-105 cursor-pointer"
                            src={honor.img || "/placeholder.svg"}
                          />
                        </a>
                    </header>
                    <div className="z-10 sm:col-span-6">
                      <div className="flex items-start justify-between mb-2">
                        <h4
                          className={`font-normal leading-snug text-gray-900 text-base ${honor.highlight ? "text-yellow-800" : ""}`}
                        >
                          {honor.title}
                        </h4>
                        <div className="flex items-center gap-2">
                          {honor.highlight && <span className="text-yellow-500">🏆</span>}
                        </div>
                      </div>
                      <p className="text-sm text-yellow-700 font-normal mb-2">{honor.description}</p>
                      <div className="mb-3">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-light leading-5 border ${honor.highlight ? "bg-yellow-200 text-yellow-900 border-yellow-300" : "bg-yellow-100 text-yellow-800 border-yellow-200"}`}
                        >
                          {honor.type}
                        </span>
                      </div>
                      <p className="text-sm leading-normal text-gray-600 font-light">{honor.details}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="mb-16 scroll-mt-16 md:mb-24 lg:mb-36 lg:scroll-mt-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
              className="text-center"
            >
              <h2 className="text-4xl md:text-6xl font-light text-gray-900 mb-8 leading-tight">
                LET'S WORK
                <br />
                <span className="text-yellow-600">TOGETHER</span>
              </h2>
              <p className="text-lg font-light text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
                Ready to bring your ideas to life? Let's collaborate and create something amazing together.
              </p>
              <Button
                size="lg"
                className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 border-0 px-12 py-4 text-lg font-light tracking-wide shadow-lg hover:shadow-yellow-500/25 transition-all duration-500 ease-out"
                asChild
              >
                <a className="flex" href="mailto:work.anni02th@gmail.com">
                  <Mail className="w-5 h-5 mr-3" />
                  work.anni02th@gmail.com
                </a>
              </Button>
            </motion.div>
          </section>

          {/* Footer */}
          <footer className="max-w-md pb-16 text-sm text-gray-500 sm:pb-0 font-light">
            <p className="leading-relaxed">
              Loosely designed in{" "}
              <a
                href="https://www.figma.com/"
                className="font-normal text-gray-600 hover:text-yellow-600 underline decoration-yellow-400 decoration-1 underline-offset-2 transition-colors duration-300"
                target="_blank"
                rel="noreferrer"
              >
                Figma
              </a>{" "}
              and coded in{" "}
              <a
                href="https://code.visualstudio.com/"
                className="font-normal text-gray-600 hover:text-yellow-600 underline decoration-yellow-400 decoration-1 underline-offset-2 transition-colors duration-300"
                target="_blank"
                rel="noreferrer"
              >
                Visual Studio Code
              </a>{" "}
              by yours truly. Built with{" "}
              <a
                href="https://react.dev/"
                className="font-normal text-gray-600 hover:text-yellow-600 underline decoration-yellow-400 decoration-1 underline-offset-2 transition-colors duration-300"
                target="_blank"
                rel="noreferrer"
              >
                React
              </a>{" "}
              and{" "}
              <a
                href="https://tailwindcss.com/"
                className="font-normal text-gray-600 hover:text-yellow-600 underline decoration-yellow-400 decoration-1 underline-offset-2 transition-colors duration-300"
                target="_blank"
                rel="noreferrer"
              >
                Tailwind CSS
              </a>
              , deployed with{" "}
              <a
                href="https://vitejs.dev/"
                className="font-normal text-gray-600 hover:text-yellow-600 underline decoration-yellow-400 decoration-1 underline-offset-2 transition-colors duration-300"
                target="_blank"
                rel="noreferrer"
              >
                Vite
              </a>
              .
            </p>
          </footer>
        </div>
      </div>
    </div>
  )
}
